import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import SpotlightBackground from "@/components/SpotlightBackground";

export default function LoginPage({ mode = "login" }) {
  const { loginJwt, signup, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const isSignup = mode === "signup";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isSignup) await signup(form.email, form.password, form.name);
      else await loginJwt(form.email, form.password);
      toast.success(isSignup ? "Welcome to OITS!" : "Welcome back!");
      nav("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  return (
    <SpotlightBackground className="min-h-[calc(100vh-140px)] flex items-center">
      <div className="max-w-md w-full mx-auto px-6 py-14">
        <div className="text-center mb-8">
          <div className="text-xs uppercase tracking-widest text-accent mb-2">{isSignup ? "Create account" : "Welcome back"}</div>
          <h1 className="heading-serif text-4xl">{isSignup ? "Join OITS" : "Sign in"}</h1>
        </div>

        <form onSubmit={submit} className="border border-border rounded-xl p-6 bg-card space-y-4" data-testid={isSignup ? "signup-form" : "login-form"}>
          {isSignup && (
            <div>
              <Label>Name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="auth-name" />
            </div>
          )}
          <div>
            <Label>Email</Label>
            <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="auth-email" />
          </div>
          <div>
            <Label>Password</Label>
            <Input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="auth-password" />
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90" data-testid="auth-submit">
            {loading ? "Please wait..." : (isSignup ? "Create account" : "Sign in")}
          </Button>
        </form>

        <div className="flex items-center gap-3 my-5">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs text-muted-foreground">or</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <Button variant="outline" onClick={loginWithGoogle} className="w-full rounded-full" data-testid="google-login-btn">
          Continue with Google
        </Button>

        <p className="text-center text-sm text-muted-foreground mt-6">
          {isSignup ? (
            <>Already have an account? <Link to="/login" className="text-accent underline-anim">Sign in</Link></>
          ) : (
            <>New here? <Link to="/signup" className="text-accent underline-anim">Create an account</Link></>
          )}
        </p>
      </div>
    </SpotlightBackground>
  );
}
